<template>
  <div v-if="visible" class="modal-overlay" @click.self="$emit('close')">
    <div class="modal-box">
      <div class="q-modal-header">
        <span class="q-modal-title">{{ titulo }}</span>
        <span v-if="dateFrom && dateTo" class="modal-rango">{{ dateFrom }} → {{ dateTo }}</span>
        <button class="modal-close" @click="$emit('close')">✕</button>
      </div>

      <div v-if="loading" class="modal-loading">Cargando...</div>

      <div v-else-if="!ventas.length" class="modal-empty">Sin ventas en el periodo seleccionado.</div>

      <div v-else class="modal-body">
        <div class="resumen-row">
          <span class="resumen-pill">{{ ventas.length }} venta{{ ventas.length !== 1 ? 's' : '' }}</span>
          <span v-if="totalEur > 0" class="resumen-pill resumen-eur">{{ fmt(totalEur) }}</span>
          <span v-if="totalUsd > 0" class="resumen-pill resumen-usd">{{ fmtUsd(totalUsd) }}</span>
        </div>

        <div class="tabla-wrap">
          <table class="tabla-ventas">
            <thead>
              <tr>
                <th>Fecha</th>
                <th>Cliente</th>
                <th>Producto</th>
                <th>Tipo</th>
                <th class="text-right">Importe</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="v in ventasPaginadas" :key="v.pedido_id + '_' + v.tipo + '_' + v.fecha">
                <td class="td-fecha">{{ fmtFecha(v.fecha) }}</td>
                <td class="td-cliente">
                  <div class="cliente-nombre">{{ v.usuario_nombre }}</div>
                  <div class="cliente-email">{{ v.usuario_email }}</div>
                </td>
                <td class="td-producto">{{ v.producto }}</td>
                <td><span :class="['tipo-badge', 'tipo-' + v.tipo]">{{ tipoLabel(v.tipo) }}</span></td>
                <td class="td-importe">
                  <span :class="v.moneda === 'USD' ? 'importe-usd' : 'importe-eur'">
                    {{ v.moneda === 'USD' ? fmtUsd(v.importe) : fmt(v.importe) }}
                  </span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <div v-if="totalPaginas > 1" class="paginacion">
          <button class="pag-btn" :disabled="pagina === 1" @click="pagina--">‹</button>
          <span class="pag-info">{{ pagina }} / {{ totalPaginas }}</span>
          <button class="pag-btn" :disabled="pagina === totalPaginas" @click="pagina++">›</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import panelesService from "@/services/paneles.service";

export default {
  name: "ModalDetalleVentas",
  props: {
    visible:    { type: Boolean, default: false },
    dateFrom:   { type: String,  default: null },
    dateTo:     { type: String,  default: null },
    tipsterId:  { type: [Number, String], default: null },
    canAdmin:   { type: Boolean, default: false },
    titulo:     { type: String, default: "Detalle de ventas" },
    productoId: { type: [Number, String], default: null },
  },
  emits: ["close"],
  data() {
    return { loading: false, ventas: [], pagina: 1 };
  },
  computed: {
    totalEur()       { return this.ventas.filter(v => v.moneda === 'EUR').reduce((s, v) => s + parseFloat(v.importe || 0), 0); },
    totalUsd()       { return this.ventas.filter(v => v.moneda === 'USD').reduce((s, v) => s + parseFloat(v.importe || 0), 0); },
    totalPaginas()   { return Math.ceil(this.ventas.length / 15); },
    ventasPaginadas() {
      const inicio = (this.pagina - 1) * 15;
      return this.ventas.slice(inicio, inicio + 15);
    },
  },
  watch: {
    visible(val) { if (val) this.cargar(); },
  },
  methods: {
    async cargar() {
      this.loading = true;
      this.ventas = [];
      this.pagina = 1;
      try {
        const payload = {};
        if (this.dateFrom)   payload.date_from   = this.dateFrom;
        if (this.dateTo)     payload.date_to     = this.dateTo;
        if (this.productoId) payload.producto_id = this.productoId;

        let res;
        if (this.canAdmin && this.tipsterId) {
          res = await panelesService.ventasDetalleAdmin(this.tipsterId, payload);
        } else {
          res = await panelesService.ventasDetalle(payload);
        }
        this.ventas = res?.data ?? [];
      } catch (e) {
        this.ventas = [];
      } finally {
        this.loading = false;
      }
    },
    fmt(n)    { return Number(n || 0).toLocaleString("es-ES", { style: "currency", currency: "EUR" }); },
    fmtUsd(n) { return Number(n || 0).toLocaleString("en-US", { style: "currency", currency: "USD" }); },
    fmtFecha(f) {
      if (!f) return "-";
      const d = new Date(f);
      return d.toLocaleDateString("es-ES", { day: "2-digit", month: "2-digit", year: "numeric" })
        + " " + d.toLocaleTimeString("es-ES", { hour: "2-digit", minute: "2-digit" });
    },
    tipoLabel(t) {
      return { compra_directa: "Compra", suscripcion_nueva: "Suscripción", renovacion: "Renovación" }[t] ?? t;
    },
  },
};
</script>

<style scoped>
.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,.55);
  z-index: 1050;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
}
.modal-box {
  background: var(--q-card);
  border: 1px solid var(--q-border);
  border-radius: 16px;
  width: 100%;
  max-width: 780px;
  max-height: 85vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}
.q-modal-header {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 16px 20px;
  border-bottom: 1px solid var(--q-border);
  flex-shrink: 0;
}
.q-modal-title { margin: 0; font-size: 15px; font-weight: 700; flex: 1; color: #d4af37; }
.modal-rango { font-size: 12px; opacity: .6; white-space: nowrap; }
.modal-close {
  background: none; border: none; cursor: pointer;
  font-size: 16px; opacity: .5; padding: 4px 6px;
  color: var(--q-text);
}
.modal-close:hover { opacity: 1; }
.modal-loading, .modal-empty {
  padding: 40px 20px;
  text-align: center;
  opacity: .6;
  font-size: 14px;
}
.modal-body { overflow-y: auto; flex: 1; }
.resumen-row {
  display: flex;
  gap: 8px;
  padding: 12px 20px;
  border-bottom: 1px solid var(--q-border);
  flex-wrap: wrap;
}
.resumen-pill {
  font-size: 12px;
  font-weight: 600;
  padding: 3px 10px;
  border-radius: 20px;
  background: rgba(128,128,128,.12);
}
.resumen-eur { background: rgba(212,175,55,.15); color: var(--q-gold); }
.resumen-usd { background: rgba(38,198,218,.15); color: #26c6da; }

.tabla-wrap { overflow-x: auto; }
.tabla-ventas {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
}
.tabla-ventas th {
  padding: 10px 16px;
  text-align: left;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: .04em;
  opacity: .55;
  border-bottom: 1px solid var(--q-border);
  white-space: nowrap;
}
.tabla-ventas td {
  padding: 10px 16px;
  border-bottom: 1px solid var(--q-border);
  vertical-align: middle;
}
.tabla-ventas tbody tr:last-child td { border-bottom: none; }
.tabla-ventas tbody tr:hover { background: rgba(128,128,128,.05); }

.td-fecha { white-space: nowrap; font-size: 12px; opacity: .8; font-variant-numeric: tabular-nums; }
.cliente-nombre { font-weight: 600; font-size: 13px; }
.cliente-email  { font-size: 11px; opacity: .55; }
.td-producto { max-width: 180px; font-size: 12px; }
.td-importe  { text-align: right; white-space: nowrap; }
.text-right  { text-align: right; }

.tipo-badge {
  display: inline-block;
  padding: 2px 9px;
  border-radius: 20px;
  font-size: 11px;
  font-weight: 600;
  white-space: nowrap;
}
.tipo-compra_directa  { background: rgba(66,165,245,.15); color: #42a5f5; }
.tipo-suscripcion_nueva { background: rgba(102,187,106,.15); color: #66bb6a; }
.tipo-renovacion       { background: rgba(212,175,55,.15); color: var(--q-gold, #d4af37); }

.importe-eur { color: var(--q-gold, #d4af37); font-weight: 700; font-variant-numeric: tabular-nums; }
.importe-usd { color: #26c6da; font-weight: 700; font-variant-numeric: tabular-nums; }

.paginacion {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 12px 20px;
  border-top: 1px solid var(--q-border);
  flex-shrink: 0;
}
.pag-btn {
  background: rgba(128,128,128,.12);
  border: 1px solid var(--q-border);
  color: var(--q-text);
  border-radius: 8px;
  width: 32px;
  height: 32px;
  font-size: 16px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background .15s;
}
.pag-btn:hover:not(:disabled) { background: rgba(212,175,55,.15); color: var(--q-gold); }
.pag-btn:disabled { opacity: .3; cursor: default; }
.pag-info { font-size: 13px; font-variant-numeric: tabular-nums; opacity: .7; min-width: 60px; text-align: center; }
</style>
